"use client"

import { useState, type ReactNode } from "react"
import { Loader2, X } from "lucide-react"

import { cn } from "@/lib/utils"
import { Field, FIELD_LABEL } from "@/components/graphite/field"
import { OverlayNav, OverlayNavAction } from "@/components/graphite/overlay-nav"
import type {
  EditableExperience,
  ProfileChange,
  ProfileDraft,
} from "@/hooks/use-candidate-profile-edit"

/**
 * Editing a candidate, from canvas artboards 14e (wide) and 14q (narrow), with
 * 14l/14t as the light pair.
 *
 * "Fields are underlines, not boxes — the profile keeps its reading rhythm and
 * the only lit underline is the one you are in. The rail is a diff: what
 * changed, from what, to what."
 *
 * Presentational only: the draft, the diff and the save all arrive as props
 * from `useCandidateProfileEdit`.
 */

/** Tags read as words with a cross, not as boxes. */
function Tags({
  tags,
  onChange,
}: {
  tags: string[]
  onChange: (tags: string[]) => void
}) {
  const [adding, setAdding] = useState("")

  const commit = () => {
    const tag = adding.trim()
    if (!tag || tags.includes(tag)) {
      setAdding("")
      return
    }
    onChange([...tags, tag])
    setAdding("")
  }

  return (
    <div>
      <div className={FIELD_LABEL}>Tags</div>
      <div className="mt-[11px] flex flex-wrap items-center gap-x-5 gap-y-2 border-b pb-[9px]">
        {tags.map((tag) => (
          <span key={tag} className="inline-flex items-center gap-[7px] text-[12px]">
            {tag}
            <button
              type="button"
              onClick={() => onChange(tags.filter((item) => item !== tag))}
              aria-label={`Remove ${tag}`}
              className="flex text-muted-foreground transition-colors hover:text-foreground"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          value={adding}
          onChange={(event) => setAdding(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === ",") {
              event.preventDefault()
              commit()
            }
          }}
          onBlur={commit}
          placeholder="add…"
          aria-label="Add a tag"
          className="min-w-[64px] flex-1 bg-transparent text-[12px] outline-none placeholder:text-muted-foreground"
        />
      </div>
    </div>
  )
}

/** 14e edits one role at a time and pages through them. */
function ExperienceEditor({
  experiences,
  index,
  onIndexChange,
  onChange,
}: {
  experiences: EditableExperience[]
  index: number
  onIndexChange: (index: number) => void
  onChange: (index: number, patch: Partial<EditableExperience>) => void
}) {
  const experience = experiences[index]
  if (!experience) return null

  // Without an id there is nothing to PATCH, so the role is shown but locked
  // rather than accepting edits that could never be saved.
  const readOnly = !experience.experienceId

  return (
    <div>
      <div className="flex items-baseline gap-3.5">
        <div className={FIELD_LABEL}>Experience</div>
        <span className="font-mono text-[10.5px] text-muted-foreground">
          {index + 1} of {experiences.length}
        </span>
        {experiences.length > 1 && (
          <span className="ml-auto flex items-center gap-3">
            <button
              type="button"
              onClick={() => onIndexChange(Math.max(0, index - 1))}
              disabled={index === 0}
              className="text-[11px] text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => onIndexChange(Math.min(experiences.length - 1, index + 1))}
              disabled={index === experiences.length - 1}
              className="text-[11px] text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
            >
              Next
            </button>
          </span>
        )}
      </div>

      <div className="mt-3.5 border-t pt-3.5">
        <div className="flex flex-col gap-5 @3xl:flex-row @3xl:gap-6">
          <Field
            controlClassName="@3xl:text-[12.5px] @3xl:leading-[1.4]"
            label="Title"
            value={experience.title}
            onChange={(value) => onChange(index, { title: value })}
            readOnly={readOnly}
            className="@3xl:flex-1"
          />
          <Field
            controlClassName="@3xl:text-[12.5px] @3xl:leading-[1.4]"
            label="Company"
            value={experience.company}
            onChange={(value) => onChange(index, { company: value })}
            readOnly={readOnly}
            className="@3xl:flex-1"
          />
          <Field
            controlClassName="@3xl:text-[12.5px] @3xl:leading-[1.4]"
            label="Since"
            value={experience.startsAt}
            onChange={(value) => onChange(index, { startsAt: value })}
            readOnly={readOnly}
            className="@3xl:w-[104px] @3xl:flex-none"
          />
        </div>

        <div className="mt-4">
          <Field
            controlClassName="@3xl:text-[12.5px] @3xl:leading-[1.4]"
            label="Description"
            value={experience.description}
            onChange={(value) => onChange(index, { description: value })}
            readOnly={readOnly}
            multiline
          />
        </div>
      </div>

      {readOnly && (
        <p className="mt-2 text-[11px] text-muted-foreground">
          This role has no stored id, so it cannot be edited here.
        </p>
      )}
    </div>
  )
}

/** 14e's rail: a changelog, not a form. */
function ChangedRail({ changes }: { changes: ProfileChange[] }) {
  return (
    <div className="hidden w-[290px] flex-none flex-col border-l px-[22px] py-5 @3xl:flex">
      <div className={FIELD_LABEL}>Changed</div>
      <div className="mt-3 min-h-0 flex-1 overflow-y-auto">
        {changes.length === 0 ? (
          <p className="border-t py-[11px] text-[11.5px] text-muted-foreground">
            Nothing changed yet.
          </p>
        ) : (
          changes.map((change) => (
            <div key={change.field} className="border-t py-[11px]">
              <div className="text-[11.5px] font-medium leading-[1.3]">
                {change.field}
              </div>
              <div className="mt-1 font-mono text-[11px] leading-[1.5] text-muted-foreground">
                {change.from} → {change.to}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

export function EditProfileView({
  draft,
  changes,
  isDirty,
  isSaving,
  onChange,
  onExperienceChange,
  onSave,
  onDiscard,
  onBack,
}: {
  draft: ProfileDraft
  changes: ProfileChange[]
  isDirty: boolean
  isSaving: boolean
  onChange: <K extends keyof ProfileDraft>(key: K, value: ProfileDraft[K]) => void
  onExperienceChange: (index: number, patch: Partial<EditableExperience>) => void
  onSave: () => void
  onDiscard: () => void
  onBack: () => void
}) {
  const [experienceIndex, setExperienceIndex] = useState(0)

  const status: ReactNode = isSaving ? (
    <span className="inline-flex items-center gap-1.5 font-mono text-[10.5px] text-muted-foreground">
      <Loader2 className="h-3 w-3 animate-spin" />
      saving
    </span>
  ) : isDirty ? (
    <span className="font-mono text-[10.5px] text-brand-ink">
      {changes.length} change{changes.length === 1 ? "" : "s"} unsaved
    </span>
  ) : (
    <span className="font-mono text-[10.5px] text-muted-foreground">saved</span>
  )

  return (
    <div className="flex h-full min-h-0">
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden px-4 py-4 @3xl:px-[26px] @3xl:py-5">
        {/* Narrow puts Save in the navbar, where the thumb already is. */}
        <OverlayNav
          className="@3xl:hidden"
          size="comfortable"
          onBack={onBack}
          backLabel="Back to the record"
          title="Edit profile"
          actions={
            <OverlayNavAction
              size="comfortable"
              tone="teal"
              onClick={onSave}
              disabled={!isDirty || isSaving}
            >
              Save
            </OverlayNavAction>
          }
        />

        <div className="flex flex-none items-baseline gap-3">
          <span className={cn(FIELD_LABEL, "hidden @3xl:inline")}>Editing profile</span>
          <span className="@3xl:ml-auto">{status}</span>
        </div>

        <div className="mt-4 min-h-0 flex-1 space-y-5 overflow-y-auto">
          <div className="flex flex-col gap-5 @3xl:flex-row @3xl:gap-6">
            <Field
              controlClassName="@3xl:text-[12.5px] @3xl:leading-[1.4]"
              label="Full name"
              value={draft.fullName}
              onChange={(value) => onChange("fullName", value)}
              className="@3xl:flex-1"
            />
            <Field
              controlClassName="@3xl:text-[12.5px] @3xl:leading-[1.4]"
              label="Location"
              value={draft.location}
              onChange={(value) => onChange("location", value)}
              className="@3xl:flex-1"
            />
          </div>

          <Field

            controlClassName="@3xl:text-[12.5px] @3xl:leading-[1.4]"
            label="Headline"
            value={draft.headline}
            onChange={(value) => onChange("headline", value)}
          />

          <Field

            controlClassName="@3xl:text-[12.5px] @3xl:leading-[1.4]"
            label="Summary"
            value={draft.summary}
            onChange={(value) => onChange("summary", value)}
            multiline
          />

          <Tags tags={draft.tags} onChange={(tags) => onChange("tags", tags)} />

          {draft.experiences.length > 0 && (
            <ExperienceEditor
              experiences={draft.experiences}
              index={Math.min(experienceIndex, draft.experiences.length - 1)}
              onIndexChange={setExperienceIndex}
              onChange={onExperienceChange}
            />
          )}
        </div>

        {/* Wide keeps the actions under the form; narrow has Save in the bar. */}
        <div className="mt-4 hidden flex-none items-center gap-[22px] border-t pt-4 @3xl:flex">
          <button
            type="button"
            onClick={onSave}
            disabled={!isDirty || isSaving}
            className="inline-flex items-center gap-[7px] text-[12.5px] font-medium text-brand-ink transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Save changes
          </button>
          <button
            type="button"
            onClick={onDiscard}
            disabled={!isDirty || isSaving}
            className="text-[12.5px] text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
          >
            Discard
          </button>
        </div>
      </div>

      <ChangedRail changes={changes} />
    </div>
  )
}
